import dayjs from 'dayjs';
import * as Yup from 'yup';

import { SurveyType } from '../../../constants/surveyTypes';
import { RequestSurveyFormValues } from './types';

export const requestSurveySelectProps = {
    size: 'md' as const,
    radius: 'lg' as const,
    variant: 'default' as const,
    searchable: true,
};

export const requestSurveyInitialValues: RequestSurveyFormValues = {
    surveyType: SurveyType.Top20,
    scopeKind: 'ZONE',
    advertisingZoneId: '',
    storeId: '',
    productListId: '',
    dueDate: null,
    dueTime: '',
    championUserId: '',
};

export const DUE_DATETIME_PAST_MESSAGE =
    'Due date and time cannot be in the past';

export function getDueDateTime(
    dueDate: Date | string | null | undefined,
    dueTime: string | undefined,
) {
    if (!dueDate || !dueTime) {
        return null;
    }

    const combined = dayjs(`${dayjs(dueDate).format('YYYY-MM-DD')} ${dueTime}`);

    if (!combined.isValid()) {
        return null;
    }

    return combined;
}

export function isDueDateTimeInTheFuture(
    dueDate: Date | string | null | undefined,
    dueTime: string | undefined,
) {
    const combined = getDueDateTime(dueDate, dueTime);

    if (!combined) {
        return false;
    }

    return combined.isAfter(dayjs());
}

export const requestSurveyValidationSchema = Yup.object()
    .shape({
        surveyType: Yup.string().required('Survey type is required'),
        scopeKind: Yup.string().oneOf(['ZONE', 'STORE']).required(),
        advertisingZoneId: Yup.string().when('scopeKind', {
            is: 'ZONE',
            then: schema => schema.required('Advertising zone is required'),
            otherwise: schema => schema.notRequired(),
        }),
        storeId: Yup.string().when('scopeKind', {
            is: 'STORE',
            then: schema => schema.required('Store is required'),
            otherwise: schema => schema.notRequired(),
        }),
        productListId: Yup.string().required('Product list is required'),
        dueDate: Yup.date()
            .nullable()
            .required('Due date is required')
            .test(
                'due-date-not-before-today',
                'Due date cannot be in the past',
                value => {
                    if (!value) {
                        return false;
                    }

                    return !dayjs(value)
                        .startOf('day')
                        .isBefore(dayjs().startOf('day'));
                },
            ),
        dueTime: Yup.string().required('Due time is required'),
        championUserId: Yup.string()
            .trim()
            .required('Please select a champion'),
    })
    .test({
        name: 'due-datetime-not-in-the-past',
        message: DUE_DATETIME_PAST_MESSAGE,
        test(values) {
            if (!values.dueDate || !values.dueTime) {
                return true;
            }

            if (isDueDateTimeInTheFuture(values.dueDate, values.dueTime)) {
                return true;
            }

            return this.createError({
                path: 'dueTime',
                message: DUE_DATETIME_PAST_MESSAGE,
            });
        },
    });
